import { useCallback, useContext } from 'react';
import { GoogleApiContext, GoogleScope } from '../../../services/gapi/gapiService';
import { LibraryContext } from '../../../services/library/libraryService';
import { Context } from '../../../services/_globalContext/context';
import { Keys, messages } from '../../../settings';

/**
 * Getting the permission that lets a sync write to YouTube playlists.
 *
 * Follows the rule the storage providers set: a popup may be opened from any
 * user gesture, a full-page redirect only from an explicit button. So on a
 * desktop the menu item that links two playlists asks straight away; on iOS,
 * where asking means leaving the page, the menu item only says where the
 * button is, and the button flushes unsaved playlist changes before it goes -
 * the link the user just made is in that queue, and a redirect would
 * otherwise lose it.
 */
export default function useYouTubeWriteAccess() {
  const ctx = useContext(Context);
  const gapiCtx = useContext(GoogleApiContext);
  const libCtx = useContext(LibraryContext);

  const ensureYouTubeWriteAccess = useCallback(
    function ensureYouTubeWriteAccess({ fromButton = false } = {}) {
      if (gapiCtx.canWriteYouTube) {
        return;
      }

      if (gapiCtx.canAuthorizeInPlace) {
        // Straight from the gesture, nothing awaited first - the popup needs it.
        gapiCtx.authorizeAdditionalScope(GoogleScope.YouTubeManage).catch(ctx.callSnackbar);
      } else if (fromButton) {
        // A redirect needs no gesture, so there is time to save first.
        libCtx
          .savePlaylists()
          .catch(ctx.callSnackbar)
          .then(() => gapiCtx.authorizeAdditionalScope(GoogleScope.YouTubeManage))
          .catch(ctx.callSnackbar);
      } else {
        ctx.callSnackbar(messages(Keys.youTubeWriteAccessHint), false);
      }
    },
    [ctx, gapiCtx, libCtx],
  );

  return {
    canWriteYouTube: gapiCtx.canWriteYouTube,
    isAuthorizing: gapiCtx.isAuthorizing,
    ensureYouTubeWriteAccess,
  };
}
